import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { SettingsTextField } from '../settings/settingsUi.jsx';
import { LEAD_STATUS_LABEL, leadStatusBadge } from '../../utils/sitePublicStatusUi.js';
import { SOURCE_OPTIONS, parseProLeadSource, buildManualSourcePath } from '../../utils/proLeadSource.js';

function toDatetimeLocal(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function ProLeadDrawer({ lead, busy, onClose, onSave, onOpenOrganization }) {
  const [status, setStatus] = useState(lead?.status || 'NEW');
  const [internalNotes, setInternalNotes] = useState(lead?.internalNotes || '');
  const [nextFollowUpAt, setNextFollowUpAt] = useState(toDatetimeLocal(lead?.nextFollowUpAt));
  const [name, setName] = useState(lead?.name || '');
  const [businessName, setBusinessName] = useState(lead?.businessName || '');
  const [city, setCity] = useState(lead?.city || '');
  const [phone, setPhone] = useState(lead?.phone || '');
  const [email, setEmail] = useState(lead?.email || '');
  const [utmSource, setUtmSource] = useState(parseProLeadSource(lead?.sourcePath).sourceKey || 'direct');

  useEffect(() => {
    setStatus(lead?.status || 'NEW');
    setInternalNotes(lead?.internalNotes || '');
    setNextFollowUpAt(toDatetimeLocal(lead?.nextFollowUpAt));
    setName(lead?.name || '');
    setBusinessName(lead?.businessName || '');
    setCity(lead?.city || '');
    setPhone(lead?.phone || '');
    setEmail(lead?.email || '');
    setUtmSource(parseProLeadSource(lead?.sourcePath).sourceKey || 'direct');
  }, [lead]);

  if (!lead) return null;

  const badge = leadStatusBadge(lead.status);
  const source = parseProLeadSource(lead.sourcePath);
  const telHref = `tel:${String(lead.phone || '').replace(/\s+/g, '')}`;
  const mailHref = `mailto:${lead.email}`;

  const persist = (patch) => {
    let sourcePath = '/professionnels';
    if (utmSource && utmSource !== 'direct') {
      sourcePath = (utmSource === 'manuel' || utmSource === 'manual' || utmSource === 'terrain')
        ? buildManualSourcePath(utmSource === 'manual' ? 'manuel' : utmSource)
        : `/professionnels?utm_source=${encodeURIComponent(utmSource)}`;
    }
    return onSave({
      status,
      internalNotes,
      nextFollowUpAt: nextFollowUpAt ? new Date(nextFollowUpAt).toISOString() : null,
      name,
      businessName,
      city,
      phone,
      email,
      sourcePath,
      ...patch,
    });
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <button type="button" className="absolute inset-0 bg-black/20" aria-label="Fermer" onClick={onClose} />
      <aside className="relative flex h-full w-full max-w-md flex-col bg-white shadow-xl">
        <header className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-4">
          <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Prospect</p>
            <h2 className="mt-0.5 truncate text-sm font-semibold text-gray-900">{lead.businessName}</h2>
            <p className="mt-0.5 text-xs text-gray-500">{lead.name} · {lead.city}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-gray-500 hover:bg-gray-50 hover:text-gray-800">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className={badge.className}>{badge.label}</span>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
              {source.label}
            </span>
          </div>

          <SettingsTextField label="Commerce / société" value={businessName} onChange={setBusinessName} />
          <SettingsTextField label="Nom du contact" value={name} onChange={setName} />
          <SettingsTextField label="Ville" value={city} onChange={setCity} />
          <SettingsTextField label="Téléphone" value={phone} onChange={setPhone} />
          <SettingsTextField label="Email" type="email" value={email} onChange={setEmail} />

          <div className="flex gap-3 text-xs">
            <a href={telHref} className="text-primary-700 hover:underline">Appeler</a>
            <a href={mailHref} className="text-primary-700 hover:underline">Écrire</a>
          </div>

          <div>
            <p className="mb-1.5 text-[13px] font-medium text-gray-600">Message original</p>
            <p className="whitespace-pre-wrap rounded-xl bg-gray-50 px-3 py-2 text-gray-800">{lead.message}</p>
          </div>

          <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium text-gray-600">Source</span>
            <select
              value={utmSource}
              onChange={(e) => setUtmSource(e.target.value)}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-primary-500"
            >
              <option value="direct">Site direct</option>
              {SOURCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium text-gray-600">Statut</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-primary-500"
            >
              {Object.entries(LEAD_STATUS_LABEL)
                .filter(([value]) => value !== 'WON')
                .map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>

          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={busy} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs" onClick={() => persist({ status: 'CONTACTED' })}>
              Marquer contacté
            </button>
            <button type="button" disabled={busy} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs" onClick={() => persist({ status: 'QUALIFIED' })}>
              Qualifier
            </button>
            <button type="button" disabled={busy} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs" onClick={() => persist({ status: 'LOST' })}>
              Perdu
            </button>
            {lead.status === 'LOST' ? (
              <button type="button" disabled={busy} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs" onClick={() => persist({ status: 'CONTACTED' })}>
                Réouvrir
              </button>
            ) : null}
          </div>
          <p className="text-xs text-gray-500">
            Pour passer en client NR2J, ouvrez la fiche société puis utilisez « Convertir en client NR2J ».
          </p>

          <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium text-gray-600">Notes internes</span>
            <textarea
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              rows={5}
              className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm outline-none focus:border-primary-500"
            />
          </label>

          <SettingsTextField
            label="Prochaine relance"
            type="datetime-local"
            value={nextFollowUpAt}
            onChange={setNextFollowUpAt}
          />

          <p className="text-xs text-gray-400">
            Créé le {new Date(lead.createdAt).toLocaleString('fr-FR')}
          </p>
        </div>

        <footer className="space-y-2 border-t border-gray-100 px-4 py-3">
          {typeof onOpenOrganization === 'function' ? (
            <button
              type="button"
              disabled={busy}
              onClick={onOpenOrganization}
              className="inline-flex w-full items-center justify-center rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-800 hover:bg-indigo-100 disabled:opacity-50"
            >
              Ouvrir la fiche société
            </button>
          ) : null}
          <button
            type="button"
            disabled={busy}
            onClick={() => persist()}
            className="inline-flex w-full items-center justify-center rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </footer>
      </aside>
    </div>
  );
}
